import { type Faq as FaqType } from 'libs/http/api/content/content.types';

import { useInt } from 'hooks/useInt';

import { Faq } from 'ui/atoms/Faq/Faq';

import styles from './CompanyDataFaq.module.scss';

type Translate = (key: string) => string;

const getFaqItems = (t: Translate): FaqType[] => [
  {
    id: 'what-data',
    question: t('What company data can an AI agent retrieve through Global Database?'),
    answer: t(
      'Identity and status, officers, shareholders, beneficial owners, group structure, financial statements and filings, read from 400+ government registries across 200+ countries and territories. Field availability varies by jurisdiction because registers publish different things.',
    ),
    default_opened: true,
  },
  {
    id: 'what-sourced-means',
    question: t('What does "sourced" mean on this page?'),
    answer: t(
      'Each value returned carries the name of the government register it was read from, the filing reference the register issued, the timestamp of the read, and a link to the original document where the register publishes one. A reviewer can open the filing independently of us.',
    ),
  },
  {
    id: 'grounding-vs-retrieval',
    question: t('What is AI grounding, and why is retrieval different?'),
    answer: t(
      'AI grounding, or LLM grounding, supplies a model with context so its answer refers to that context rather than to training data alone. The model still composes the answer from its weights and can still produce a value that appears in no source. Retrieval, as Regis and the structured endpoints do it, returns the registry record itself with its provenance. Regis holds no company facts in its weights, so an answer without a retrieved record is an empty answer, not an invented one.',
    ),
  },
  {
    id: 'kya-vs-kyba',
    question: t('What is Know Your Agent, and how is KYBA different?'),
    answer: t(
      'Know Your Agent (KYA) authenticates the software: which agent is this and is it authorised. Know Your Business Agent (KYBA) asks the question behind it: which legal entity is responsible for this agent, is that entity registered and active, who owns it and who may act for it. KYA is an identity problem. KYBA is a registry problem, and this dataset is what the verifying agent retrieves to answer it.',
    ),
  },
  {
    id: 'software-or-source',
    question: t('Is this a KYB software or KYB solution, or a data source for one?'),
    answer: t(
      'A data source, delivered as API, MCP server, Regis and bulk feed. Teams building or buying KYB software use it as the registry layer underneath, so that every check the software runs resolves to a filing. It is not a workflow tool and does not replace case management.',
    ),
  },
  {
    id: 'which-route',
    question: t('Which delivery route should an agent use?'),
    answer: t(
      'MCP if the agent runs in Claude or an MCP-compatible framework. REST if a pipeline already knows the field it needs. The Regis API if the input is a question rather than a field. Bulk if you index company data into your own retrieval layer. All four read the same records.',
    ),
  },
  {
    id: 'mcp-connectors',
    question: t('Does the MCP server work with Claude, OpenAI and Microsoft Copilot?'),
    answer: t(
      'Yes. There are connectors for Claude, OpenAI and Microsoft Copilot, and because it is a standard MCP server it also connects to any other MCP client or in-house agent framework. Setup details are on the MCP server page.',
    ),
  },
  {
    id: 'missing-field',
    question: t('What happens when a register does not publish a field?'),
    answer: t(
      'The response states that the field is not published by that register for that entity type, and names the register. No estimate, modelled value or figure from a similar company is substituted.',
    ),
  },
  {
    id: 'freshness',
    question: t('How current is the data?'),
    answer: t(
      'Records are synced daily. Registers publish on their own schedules, so each value carries a retrieval timestamp showing what the register held at the time of the read.',
    ),
  },
  {
    id: 'redistribution-training',
    question: t('Can we redistribute the data, or train a model on it?'),
    answer: t(
      'Both are available under licence. Standard access covers internal use. Redistribution, meaning serving the data to third parties inside your product or through your agent, is licensed separately. Model training and retrieval use is also licensed, with the scope written into the contract. Ask us for the terms before you build against them.',
    ),
  },
  {
    id: 'eu-ai-act',
    question: t('Does this help with EU AI Act logging obligations?'),
    answer: t(
      "The response carries the resolved entity, the fields used, the register and the retrieval timestamp in structured form, which is the material an Article 12 event log needs to reconstruct a decision. Retention and the rest of the compliance framework remain the deployer's responsibility.",
    ),
  },
  {
    id: 'security-docs',
    question: t('What security and compliance documentation is available?'),
    answer: t(
      'ISO 27001 certification and UK ICO registration. Security documentation and the data processing agreement are available under NDA before contract.',
    ),
  },
];

export const CompanyDataFaq = () => {
  const { t } = useInt();

  return <Faq className={styles.faq} title={t('Common questions.')} data={getFaqItems(t)} />;
};
